/**
 * Persistent left sidebar — Notion-inspired navigation rail.
 *
 * Phase 5 (2026-05-15): boards are grouped by project. Each project
 * header collapses/expands and exposes a context menu (Rename / Color /
 * Members / Archive). A "+ New project" affordance lives at the bottom
 * of the section.
 *
 * Round 3 (2026-05-15): resizable width via drag-handle on the right
 * edge, plus a collapse/expand toggle in the top-right corner. Both
 * states persist to localStorage:
 *   - chaos-sidebar-width  →  numeric px (clamped 200..360)
 *   - chaos-sidebar-collapsed → 'true' | 'false'
 *
 * Strict ADR-057: monochrome surfaces only. Project color is a
 * user-controlled status-taxonomy tint (small dot in the header), never
 * applied to the row chrome itself.
 */
import type { Project } from '@chaos/types/project';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useMatchRoute, useNavigate } from '@tanstack/react-router';
import {
  Bell,
  ChevronRight,
  ChevronsLeft,
  ChevronsUpDown,
  LogOut,
  MessageSquare,
  Moon,
  MoreHorizontal,
  Plus,
  Settings,
  Sun,
} from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { authApi } from '../../lib/api';
import { kanbanApi, kanbanKeys } from '../../lib/api/kanban';
import { projectKeys, projectsApi } from '../../lib/api/projects';
import { colorTint, normaliseColor } from '../../lib/status-color';
import { useResizablePanel } from '../../lib/use-resizable-panel';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth-store';
import { useThemeStore } from '../../stores/theme-store';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const SIDEBAR_PROJECT_EXPANSION = 'chaos-sidebar-project-expansion';

function loadExpansion(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(SIDEBAR_PROJECT_EXPANSION);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed as Record<string, boolean>;
  } catch {
    /* fall through */
  }
  return {};
}

function saveExpansion(map: Record<string, boolean>): void {
  try {
    localStorage.setItem(SIDEBAR_PROJECT_EXPANSION, JSON.stringify(map));
  } catch {
    /* ignore — quota exhausted etc. */
  }
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { t } = useTranslation();
  const matchRoute = useMatchRoute();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const user = useAuthStore((s) => s.user);
  const workspaces = useAuthStore((s) => s.workspaces);
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const setUnauthenticated = useAuthStore((s) => s.setUnauthenticated);
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);

  const activeWorkspace = workspaces.find((w) => w.workspace.id === activeWorkspaceId)?.workspace;
  const activeRole = workspaces.find((w) => w.workspace.id === activeWorkspaceId)?.membership.role;
  const canCreateBoards =
    activeRole !== undefined && activeRole !== 'auditor' && activeRole !== 'external';
  const canCreateProjects = activeRole === 'founder' || activeRole === 'admin';

  const { width, isDragging, onHandleMouseDown } = useResizablePanel({
    storageKey: 'chaos-sidebar-width',
    defaultWidth: 240,
    minWidth: 200,
    maxWidth: 360,
    side: 'left',
  });

  const projectsQ = useQuery({
    queryKey: projectKeys.list(),
    queryFn: () => projectsApi.list(),
  });

  const boardsQ = useQuery({
    queryKey: kanbanKeys.boards(),
    queryFn: () => kanbanApi.listBoards(),
  });

  const createBoardM = useMutation({
    mutationFn: (input: { name: string; projectId: string }) =>
      kanbanApi.createBoard({ name: input.name, projectId: input.projectId }),
    onSuccess: async (board) => {
      await qc.invalidateQueries({ queryKey: kanbanKeys.boards() });
      await navigate({ to: '/board/$boardId', params: { boardId: board.id } });
    },
  });

  const createProjectM = useMutation({
    mutationFn: (name: string) => projectsApi.create({ name }),
    onSuccess: async (project) => {
      await qc.invalidateQueries({ queryKey: projectKeys.list() });
      await navigate({ to: '/project/$projectId', params: { projectId: project.id } });
    },
  });

  const archiveProjectM = useMutation({
    mutationFn: (projectId: string) => projectsApi.archive(projectId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: projectKeys.list() });
      await qc.invalidateQueries({ queryKey: kanbanKeys.boards() });
    },
  });

  const [expansion, setExpansion] = React.useState<Record<string, boolean>>(() => loadExpansion());
  React.useEffect(() => {
    saveExpansion(expansion);
  }, [expansion]);

  const [newBoardProjectId, setNewBoardProjectId] = React.useState<string | null>(null);
  const [boardDraft, setBoardDraft] = React.useState('');
  const boardInputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (newBoardProjectId) boardInputRef.current?.focus();
  }, [newBoardProjectId]);

  const [creatingProject, setCreatingProject] = React.useState(false);
  const [projectDraft, setProjectDraft] = React.useState('');
  const projectInputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (creatingProject) projectInputRef.current?.focus();
  }, [creatingProject]);

  const submitBoard = async () => {
    const trimmed = boardDraft.trim();
    if (!trimmed || !newBoardProjectId) {
      setNewBoardProjectId(null);
      setBoardDraft('');
      return;
    }
    await createBoardM.mutateAsync({ name: trimmed, projectId: newBoardProjectId });
    setBoardDraft('');
    setNewBoardProjectId(null);
  };

  const submitProject = async () => {
    const trimmed = projectDraft.trim();
    if (!trimmed) {
      setCreatingProject(false);
      return;
    }
    await createProjectM.mutateAsync(trimmed);
    setProjectDraft('');
    setCreatingProject(false);
  };

  const handleSignOut = async () => {
    try {
      await authApi.logout();
    } catch {
      /* ignore — proceed to clear local state */
    }
    setUnauthenticated();
    window.location.href = '/login';
  };

  const projects = projectsQ.data ?? [];
  const allBoards = boardsQ.data ?? [];

  const boardsByProject = React.useMemo(() => {
    const map = new Map<string, typeof allBoards>();
    for (const b of allBoards) {
      const list = map.get(b.projectId) ?? [];
      list.push(b);
      map.set(b.projectId, list);
    }
    return map;
  }, [allBoards]);

  // Boards whose project isn't visible to us (e.g. via direct
  // board_member grant) — surfaced as "Shared boards" group.
  const orphanBoards = React.useMemo(() => {
    const projectIds = new Set(projects.map((p) => p.id));
    return allBoards.filter((b) => !projectIds.has(b.projectId));
  }, [allBoards, projects]);

  // Collapsed: render zero-width spacer; the Shell handles the
  // "expand" handle so we don't double-paint a button here.
  if (collapsed) return null;

  const toggleExpansion = (projectId: string) => {
    setExpansion((prev) => ({ ...prev, [projectId]: !(prev[projectId] ?? true) }));
  };

  return (
    <aside
      aria-label="Primary navigation"
      style={{ width }}
      className={cn(
        'relative flex h-screen shrink-0 flex-col border-r border-border bg-muted/30',
        isDragging && 'select-none',
      )}
    >
      {/* Workspace header */}
      <div className="flex items-center gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="group flex flex-1 items-center justify-between px-3 py-2.5 text-left hover:bg-foreground/5"
              aria-label="Account menu"
            >
              <div className="flex min-w-0 flex-col leading-tight">
                <span className="truncate text-[13px] font-semibold tracking-tight text-foreground">
                  {activeWorkspace?.name ?? user?.displayName ?? user?.email ?? t('app.name')}
                </span>
                <span className="truncate text-[11px] text-muted-foreground">
                  {user?.email ?? ''}
                </span>
              </div>
              <ChevronsUpDown
                className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                aria-hidden
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="bottom" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">{user?.displayName ?? user?.email}</span>
                <span className="text-xs text-muted-foreground">{user?.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              <span>{theme === 'dark' ? t('nav.light_mode') : t('nav.dark_mode')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings/profile">
                <Settings className="h-4 w-4" />
                <span>{t('settings.profile.title')}</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings/members">
                <Settings className="h-4 w-4" />
                <span>{t('settings.members.title')}</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings/notifications">
                <Bell className="h-4 w-4" />
                <span>{t('settings.notifications.title')}</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings/telegram">
                <MessageSquare className="h-4 w-4" />
                <span>{t('settings.telegram.title')}</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings/workspace">
                <Settings className="h-4 w-4" />
                <span>{t('settings.workspace.title', { defaultValue: 'Workspace' })}</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut}>
              <LogOut className="h-4 w-4" />
              <span>{t('dashboard.sign_out')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          type="button"
          onClick={onToggle}
          aria-label="Collapse sidebar"
          className="mr-1.5 flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
        >
          <ChevronsLeft className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 pb-2 pt-3">
        <SectionLabel>{t('nav.kanban_section')}</SectionLabel>

        <ul className="mt-1 flex flex-col">
          {projectsQ.isLoading && (
            <li className="px-2 py-1 text-xs text-muted-foreground">{t('common.loading')}</li>
          )}

          {projects.map((project) => {
            const boards = boardsByProject.get(project.id) ?? [];
            const isOpen = expansion[project.id] ?? true;
            return (
              <li key={project.id}>
                <ProjectHeader
                  project={project}
                  isOpen={isOpen}
                  canManage={canCreateProjects}
                  onToggle={() => toggleExpansion(project.id)}
                  onArchive={async () => {
                    if (window.confirm(`Archive “${project.name}”?`)) {
                      await archiveProjectM.mutateAsync(project.id);
                    }
                  }}
                />
                {isOpen && (
                  <ul className="mt-0.5 flex flex-col">
                    {boards.map((board) => {
                      const isActive = !!matchRoute({
                        to: '/board/$boardId',
                        params: { boardId: board.id },
                        fuzzy: true,
                      });
                      return (
                        <li key={board.id}>
                          <Link
                            to="/board/$boardId"
                            params={{ boardId: board.id }}
                            className={cn(
                              'flex items-center rounded px-2 py-1 text-[13px] transition-colors',
                              'ml-3',
                              isActive
                                ? 'bg-foreground/5 text-foreground'
                                : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
                            )}
                          >
                            <span className="truncate">{board.name}</span>
                          </Link>
                        </li>
                      );
                    })}

                    {canCreateBoards && newBoardProjectId === project.id && (
                      <li className="ml-3 py-0.5">
                        <input
                          ref={boardInputRef}
                          value={boardDraft}
                          onChange={(e) => setBoardDraft(e.target.value)}
                          onBlur={() => {
                            if (!boardDraft.trim()) {
                              setNewBoardProjectId(null);
                              setBoardDraft('');
                            }
                          }}
                          onKeyDown={async (e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              await submitBoard();
                            }
                            if (e.key === 'Escape') {
                              setBoardDraft('');
                              setNewBoardProjectId(null);
                            }
                          }}
                          placeholder={t('kanban.boards.placeholder')}
                          aria-label={t('kanban.boards.placeholder')}
                          className="w-full rounded bg-background px-2 py-1 text-[13px] outline-none ring-1 ring-border focus-visible:ring-foreground/40"
                        />
                      </li>
                    )}

                    {canCreateBoards && newBoardProjectId !== project.id && (
                      <li className="ml-3">
                        <button
                          type="button"
                          onClick={() => {
                            setNewBoardProjectId(project.id);
                          }}
                          className="flex w-full items-center gap-1.5 rounded px-2 py-1 text-[13px] text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
                        >
                          <Plus className="h-3.5 w-3.5" aria-hidden />
                          <span>{t('nav.new_board')}</span>
                        </button>
                      </li>
                    )}

                    {boards.length === 0 &&
                      !canCreateBoards &&
                      newBoardProjectId !== project.id && (
                        <li className="ml-3 px-2 py-1 text-xs text-muted-foreground/70">
                          {t('nav.no_boards')}
                        </li>
                      )}
                  </ul>
                )}
              </li>
            );
          })}

          {orphanBoards.length > 0 && (
            <li className="mt-2">
              <SectionLabel>{t('nav.shared_section')}</SectionLabel>
              <ul className="mt-0.5 flex flex-col">
                {orphanBoards.map((board) => {
                  const isActive = !!matchRoute({
                    to: '/board/$boardId',
                    params: { boardId: board.id },
                    fuzzy: true,
                  });
                  return (
                    <li key={board.id}>
                      <Link
                        to="/board/$boardId"
                        params={{ boardId: board.id }}
                        className={cn(
                          'flex items-center rounded px-2 py-1 text-[13px] transition-colors',
                          isActive
                            ? 'bg-foreground/5 text-foreground'
                            : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
                        )}
                      >
                        <span className="truncate">{board.name}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </li>
          )}

          {projectsQ.data &&
            projects.length === 0 &&
            orphanBoards.length === 0 &&
            !creatingProject && (
              <li className="px-2 py-1 text-xs text-muted-foreground">{t('nav.no_projects')}</li>
            )}

          {canCreateProjects && creatingProject && (
            <li className="mt-1 px-1 py-0.5">
              <input
                ref={projectInputRef}
                value={projectDraft}
                onChange={(e) => setProjectDraft(e.target.value)}
                onBlur={() => {
                  if (!projectDraft.trim()) setCreatingProject(false);
                }}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    await submitProject();
                  }
                  if (e.key === 'Escape') {
                    setProjectDraft('');
                    setCreatingProject(false);
                  }
                }}
                placeholder={t('nav.project_placeholder')}
                aria-label={t('nav.project_placeholder')}
                className="w-full rounded bg-background px-2 py-1 text-[13px] outline-none ring-1 ring-border focus-visible:ring-foreground/40"
              />
            </li>
          )}

          {canCreateProjects && !creatingProject && (
            <li className="mt-1">
              <button
                type="button"
                onClick={() => setCreatingProject(true)}
                className="flex w-full items-center gap-1.5 rounded px-2 py-1 text-[13px] text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
              >
                <Plus className="h-3.5 w-3.5" aria-hidden />
                <span>{t('nav.new_project')}</span>
              </button>
            </li>
          )}
        </ul>
      </nav>

      {/* Resize handle on the right edge */}
      <button
        type="button"
        aria-label="Resize sidebar"
        tabIndex={-1}
        onMouseDown={onHandleMouseDown}
        className={cn(
          'absolute right-0 top-0 z-10 h-full w-1 cursor-col-resize border-0 bg-transparent p-0',
          'transition-colors',
          isDragging ? 'bg-foreground/20' : 'hover:bg-foreground/10',
        )}
      />
    </aside>
  );
}

interface ProjectHeaderProps {
  project: Project;
  isOpen: boolean;
  canManage: boolean;
  onToggle: () => void;
  onArchive: () => void;
}

function ProjectHeader({ project, isOpen, canManage, onToggle, onArchive }: ProjectHeaderProps) {
  const tint = colorTint(normaliseColor(project.color));
  return (
    <div className="group/project flex items-center pr-1">
      <button
        type="button"
        onClick={onToggle}
        className="flex flex-1 items-center gap-1 rounded px-1 py-1 text-left text-[12px] font-medium tracking-tight text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
      >
        <ChevronRight
          className={cn('h-3 w-3 shrink-0 transition-transform', isOpen && 'rotate-90')}
          aria-hidden
        />
        <span
          aria-hidden
          className="h-1.5 w-1.5 shrink-0 rounded-full"
          style={{ backgroundColor: tint.foreground }}
        />
        <span className="truncate">{project.name}</span>
      </button>
      <Link
        to="/project/$projectId"
        params={{ projectId: project.id }}
        aria-label={`Open ${project.name}`}
        className="rounded p-0.5 text-muted-foreground/0 transition-opacity hover:bg-foreground/5 hover:text-foreground group-hover/project:text-muted-foreground"
      >
        <Settings className="h-3 w-3" aria-hidden />
      </Link>
      {canManage && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="rounded p-0.5 text-muted-foreground/0 transition-opacity hover:bg-foreground/5 hover:text-foreground group-hover/project:text-muted-foreground"
              aria-label={`Manage ${project.name}`}
            >
              <MoreHorizontal className="h-3 w-3" aria-hidden />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="right" className="w-44">
            <DropdownMenuItem asChild>
              <Link to="/project/$projectId" params={{ projectId: project.id }}>
                <Settings className="h-4 w-4" />
                <span>Open project</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onArchive}>
              <span className="text-error">Archive</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80">
      {children}
    </div>
  );
}
